import {Map, List} from 'immutable';
import moment from 'moment';
import assignmentConstants from 'client/Assignments/constants';
import {stats, BUCKETS, getBucketByAccuracy} from 'lib/StatsUtil';
import {humanReadableDurationString} from 'lib/timeUtils';
import systemTimeOffsetStore from 'client/generic/SystemTimeOffset/SystemTimeOffset.store';
import constants from 'client/constants';
import * as TimeUtil from 'lib/timeUtils';
import type {
  QuestionSetModelV1,
  AssignmentModelV3,
  StudentClassroomModelV1
} from '@albert-io/models';
import type {QuestionType} from 'resources/Question/Question.model';

interface IdsToSelect {
  classroomId?: string;
  studentId?: string | null;
}

interface StudentAssignmentRelationship {
  getStartTime(): string | null;
  getSubmitted(): string;
  [key: string]: unknown;
}

/**
 * Subset of the dynamically-generated `assignment_v*.meta` getters that this file accesses through
 * `this.getMeta()`. The meta model is generated from JSON:API metadata config and is untyped at the
 * `.js` source, so we declare the precise shape we rely on here.
 */
interface AssignmentMeta {
  getCurriculumColors(): string | null;
  getStudentCountOfCorrectGuesses(): number;
  getStudentCountOfGuesses(): number;
  getCountOfQuestions(): number;
  getStudentSubmittedAt(): string | null;
  getStudentTimeSpent(): number;
  isStudentSubmitted(): boolean;
}

/**
 * Subset of a classroom/student-classroom relationship model that this file touches via
 * `getClassrooms()` / `getStudentClassrooms()` elements.
 */
interface ClassroomRelation {
  getId(): string;
  getName(): string;
  // With an id, returns the single matching assignment relationship; without, the full list.
  getAssignmentRelationships(id: string): AssignmentRelation;
  getAssignmentRelationships(): List<AssignmentRelation>;
}

/** Subset of an assignment-relationship model reached from a classroom/student. */
interface AssignmentRelation {
  getSettings(): Map<string, unknown> | undefined;
}

/** Subset of a student relationship model reached via `getStudents()`. */
interface StudentRelation {
  getId(): string;
  get(key: string): Map<string, unknown> | undefined;
  getMeta(): {
    getAssignmentCountOfCorrectGuesses(): number;
    getAssignmentCountOfIncorrectGuesses(): number;
  };
  getAssignmentRelationships(): Map<string, AssignmentRelation>;
}

/**
 * `this` inside the extension methods is an `AssignmentModelV3` instance that has been augmented
 * via `AssignmentModelV3.extend(assignmentExtensions)`. `AssignmentModelV3` extends the dynamic
 * `GenericModel` base (typed as `any`-constructor in `.js`), so the model/relationship getters this
 * file reaches through `this` are not statically visible. We declare the ones we use here with
 * honest types; values that are genuinely dynamic JSON:API data are typed `unknown` and narrowed at
 * the use site. We then apply this as the `this` context via `ThisType`.
 */
interface AssignmentExtensionContext extends AssignmentModelV3 {
  // Dynamic model / relationship getters reached through `this`.
  getMeta(): AssignmentMeta;
  getId(): string;
  getStatus(): string;
  getSettings(): Map<string, unknown>;
  getInsertedAt(): moment.Moment;
  getUpdatedAt(): moment.Moment;
  getClassrooms(): List<ClassroomRelation>;
  getStudentClassrooms(): List<ClassroomRelation>;
  getStudents(): List<StudentRelation>;
  getQuestionSets(): List<QuestionSetModelV1>;
  getClassroomRelationships(classroomId?: string): AssignmentRelation | undefined;
  get(key: string): Map<string, unknown> | undefined;
  cache<T>(key: string, func: () => T): T;
  setField(field: string, value: unknown): AssignmentModelV3;
  // Extension methods declared/used through `this`.
  getSettingsFromRelations(idsToSelect?: IdsToSelect): Map<string, unknown> | undefined;
  getSortedQuestionSets(): List<QuestionSetModelV1>;
  getSortedQuestions(): List<QuestionType>;
  getDueDateFromSettings(idsToSelect?: IdsToSelect): moment.Moment;
  getStartDateFromSettings(idsToSelect?: IdsToSelect): moment.Moment;
  getTimeLimitFromSettings(idsToSelect?: IdsToSelect): number | null;
  getCorrectAnswerSettingFromSettings(idsToSelect?: IdsToSelect): string;
  getIsAllowLateSubmissionsFromSettings(idsToSelect?: IdsToSelect): boolean;
  getStudentStartTime(studentId?: string | null): string | null;
  getStudentSubmittedAt(studentId?: string | null): string;
  hasStudentStartedAssignment(studentId?: string | null): boolean;
  hasStudentSubmittedAssignment(studentId?: string | null): boolean;
  isAssigned(): boolean;
  isPastDue(idsToSelect?: IdsToSelect): boolean;
  isScheduled(idsToSelect?: IdsToSelect): boolean;
  isDueIn(duration: number, timeframe: string, idsToSelect?: IdsToSelect): boolean;
}

const assignmentExtensions = {
  getClassroomNames(): string {
    return this.getClassrooms()
      .map((classroom) => classroom!.getName())
      .join(', ');
  },

  getCountOfStudents(): number {
    return this.getStudents().size;
  },

  getCurriculumColorsArray(): string[] {
    const curriculumColors = this.getMeta().getCurriculumColors();
    return curriculumColors ? curriculumColors.split(',') : [];
  },

  getSortedQuestionSets(): List<QuestionSetModelV1> {
    const func = () =>
      // Immutable 3.8 types `List.sortBy` as returning the wider `Iterable`; the runtime value is a
      // sorted List, so convert back to `List` to keep the declared return type honest.
      this.getQuestionSets()
        .sortBy((questionSet) =>
          questionSet.getIn(['relationshipMeta', 'assignment', this.getId(), 'position'], 0)
        )
        .toList();
    return this.cache('getSortedQuestionSets', func);
  },

  getQuestionSetForActiveQuestion(questionId: string): QuestionSetModelV1 | undefined {
    return this.getQuestionSets().find((set) => {
      const questions = set!.getQuestions();
      const matchingSet = questions.find((question) => {
        return question.getId() === questionId;
      });
      return Boolean(matchingSet);
    });
  },

  getSortedQuestions(): List<QuestionType> {
    const sortedQuestionsFunc = () => {
      return this.getSortedQuestionSets().reduce(
        (acc: List<QuestionType> | undefined, questionSet): List<QuestionType> => {
          // Immutable 3.8 types both the reduce accumulator and value params as optional. The
          // accumulator is never undefined at runtime (an initial value is always passed) and
          // present List elements are always defined. `concat` is typed as the wider `Iterable`
          // here, so narrow back to `List<QuestionType>`.
          return (acc ?? List<QuestionType>()).concat(
            questionSet!.getQuestions()
          ) as List<QuestionType>;
        },
        List<QuestionType>()
      );
    };
    return this.cache('getSortedQuestions', sortedQuestionsFunc);
  },

  // getters from settings col
  // will fetch settings from student assignment based on studentId
  // or from classroom settings based on classroomId
  // or from assignment settings if no studentId or classroomId is provided
  // or the base assignment setting columns
  getSettingsFromRelations(idsToSelect: IdsToSelect = {}) {
    const {classroomId, studentId} = idsToSelect;

    // student settings should always be returned first
    if (studentId) {
      const students = this.getStudents();
      // Co-assigned students not included in the current query denormalize to null, so
      // optional-chain the id read instead of asserting non-null (crashed on `.getId()`,
      // HB #131970707); a null element simply fails the match.
      const student = students.find((st) => st?.getId() === studentId);
      // `relationshipMeta` is a dynamically-nested Immutable Map (JSON:API relationship metadata);
      // walk it as nested `Map<string, unknown>` values.
      const studentAssignmentRelationships = student?.get('relationshipMeta')?.get('assignment') as
        | Map<string, unknown>
        | undefined;
      const studentAssignment = studentAssignmentRelationships?.get(this.getId()) as
        | Map<string, unknown>
        | undefined;
      const settings = studentAssignment?.get('settings') as Map<string, unknown> | undefined;
      if (settings && (!Map.isMap(settings) || settings.size > 0)) {
        return settings;
      }
    }
    if (classroomId) {
      const classrooms = this.getClassrooms().isEmpty()
        ? this.getStudentClassrooms()
        : this.getClassrooms();
      const classroom = (classrooms || List<ClassroomRelation>()).find(
        (cr) => cr?.getId() === classroomId
      );
      const classroomAssignment =
        classroom?.getAssignmentRelationships?.(this.getId()) ||
        this.getClassroomRelationships(classroomId);
      const settings = classroomAssignment?.getSettings?.();
      if (settings && (!Map.isMap(settings) || settings.size > 0)) {
        return settings;
      }
    }
    return this.getSettings();
  },

  getIsAllowTranslationFromSettings(idsToSelect: IdsToSelect = {}): boolean {
    const settings = this.getSettingsFromRelations(idsToSelect);
    // JSON:API settings values are dynamic (typed `unknown`); the domain contract is boolean.
    return settings?.get('translation_access') as boolean;
  },

  getLockdownModeEnabledFromSettings(idsToSelect: IdsToSelect = {}): boolean {
    const settings = this.getSettingsFromRelations(idsToSelect);
    return (settings?.get('lockdown_mode_enabled') as boolean) ?? false;
  },

  getIsAllowLateSubmissionsFromSettings(idsToSelect: IdsToSelect = {}): boolean {
    const settings = this.getSettingsFromRelations(idsToSelect);
    return settings?.get('allow_late_submissions') as boolean;
  },

  getIsAutoAssignNewClassroomEnrolleesFromSettings(idsToSelect: IdsToSelect = {}): boolean {
    const settings = this.getSettingsFromRelations(idsToSelect);
    return settings?.get('auto_assign_new_classroom_enrollees') as boolean;
  },

  getCorrectAnswerSettingFromSettings(idsToSelect: IdsToSelect = {}): string {
    const settings = this.getSettingsFromRelations(idsToSelect);
    return settings?.get('correct_answer_setting') as string;
  },

  getDueDateFromSettings(idsToSelect: IdsToSelect = {}): moment.Moment {
    // assignments list has due dates coming from meta
    // not using the extensions since they return `now`
    // if undefined
    const metaDueDate =
      this.get('meta')?.get('student_settings_due_date') ||
      this.get('meta')?.get('classroom_settings_due_date');

    if (metaDueDate) {
      // Dynamic JSON:API meta value; treated as a moment-compatible due date by callers.
      return metaDueDate as moment.Moment;
    }

    const settings = this.getSettingsFromRelations(idsToSelect);
    const settingsValue = settings?.get?.('due_date');
    const momentValue = settingsValue ? moment(settingsValue as moment.MomentInput) : undefined;
    const dueDate = momentValue?.isValid?.() ? momentValue : settingsValue;
    return dueDate as moment.Moment;
  },

  getStartDateFromSettings(idsToSelect: IdsToSelect = {}): moment.Moment {
    const settings = this.getSettingsFromRelations(idsToSelect);
    const settingsValue = settings?.get?.('start_date');
    const momentValue = settingsValue ? moment(settingsValue as moment.MomentInput) : undefined;
    const startDate = momentValue?.isValid?.() ? momentValue : settingsValue;
    return startDate as moment.Moment;
  },

  getTimeLimitFromSettings(idsToSelect: IdsToSelect = {}): number | null {
    const settings = this.getSettingsFromRelations(idsToSelect);
    return settings?.get('time_limit') as number | null;
  },

  getIsRandomizeQuestionsFromSettings(idsToSelect: IdsToSelect = {}): boolean {
    const settings = this.getSettingsFromRelations(idsToSelect);
    return settings?.get('randomize_question_order') as boolean;
  },

  getMessageFromSettings(idsToSelect: IdsToSelect = {}): string {
    const settings = this.getSettingsFromRelations(idsToSelect);
    return settings?.get('message') as string;
  },

  getLtiLineItemIdFromSettings(idsToSelect: IdsToSelect = {}): string {
    const settings = this.getSettingsFromRelations(idsToSelect);
    return settings?.get('lti_line_item_id') as string;
  },

  // This method doesn't use idsToSelect like other "fromSettings" methods since Partial Credit
  // cannot be overridden on the classroom/student level.
  getPartialCreditEnabledFromSettings(): boolean {
    return this.getSettings().get('partial_credit_enabled', false) as boolean;
  },

  hasSections(): boolean {
    return false;
  },

  hasStartDate(idsToSelect: IdsToSelect = {}) {
    const insertedAtDateAndStartDateAreSame = this.getInsertedAt().isSame(
      this.getStartDateFromSettings(idsToSelect),
      'minute'
    );
    const updatedAtDateAndStartDateAreSame = this.getUpdatedAt().isSame(
      this.getStartDateFromSettings(idsToSelect),
      'minute'
    );
    return !updatedAtDateAndStartDateAreSame && !insertedAtDateAndStartDateAreSame;
  },

  isPastDueDate(idsToSelect: IdsToSelect = {}): boolean {
    return this.getDueDateFromSettings(idsToSelect).isBefore(
      systemTimeOffsetStore.getCurrentTime()
    );
  },

  isAssigned(): boolean {
    return this.getStatus() === 'assigned';
  },

  isOpen(idsToSelect: IdsToSelect = {}): boolean {
    return (
      this.isAssigned() &&
      !this.isScheduled(idsToSelect) &&
      (this.getIsAllowLateSubmissionsFromSettings(idsToSelect) === true ||
        this.isPastDue(idsToSelect) === false)
    );
  },

  getQuestionCountsByDifficulty(): Record<string, number> {
    const func = () => {
      const difficulties = constants.QUESTION_IMPORT.DIFFICULTIES;
      const mirrored = Object.entries(difficulties).reduce<Record<string, string>>(
        (acc, [label, value]) => {
          acc[String(value)] = label;
          return acc;
        },
        {}
      );
      const startingCount = Object.keys(difficulties).reduce<Record<string, number>>((acc, key) => {
        acc[key] = 0;
        return acc;
      }, {});
      return this.getSortedQuestions().reduce(
        (acc: Record<string, number> | undefined, question): Record<string, number> => {
          // Immutable 3.8 types both the reduce accumulator and value params as optional. `acc` is
          // never undefined at runtime (an initial value is always passed) and `question` is non-null
          // for present List elements. `getDifficulty` exists at runtime on every QuestionType
          // variant; a few variants are missing the declaration-merged SharedQuestionMethods in
          // Question.model.
          const counts = acc ?? startingCount;
          const difficulty = (question as QuestionType & {getDifficulty(): number}).getDifficulty();
          counts[mirrored[String(difficulty)]]++;
          return counts;
        },
        startingCount
      );
    };
    return this.cache('getQuestionCountsByDifficulty', func);
  },

  // logic used to identify the classroom settings to pick for students
  // that have one assignment for multiple classes that they are a part of
  getClassroomWithLatestAssignmentDueDate(): StudentClassroomModelV1 {
    const classrooms = this.getClassrooms().isEmpty()
      ? this.getStudentClassrooms()
      : this.getClassrooms();

    const classroom = (classrooms || List<ClassroomRelation>()).reduce(
      // Immutable 3.8 types the reduce accumulator (`reduction`) as optional, so accept `undefined`
      // for `latest`; the `!latest` guard below already handles it.
      (latest: ClassroomRelation | null | undefined, curr: ClassroomRelation | undefined) => {
        if (!latest) {
          return curr ?? null;
        }

        const currAssignment = curr?.getAssignmentRelationships().first();
        const latestAssignment = latest.getAssignmentRelationships().first();

        if (currAssignment && latestAssignment) {
          // `getSettingsFromRelations` does not exist on these assignment variables
          // but they should be scoped already by the request
          const latestAssignmentSettings = latestAssignment.getSettings();
          const currAssignmentSettings = currAssignment.getSettings();

          const latestDueDate = new Date(
            (latestAssignmentSettings?.get('due_date') as string | number) || 0
          );
          const currentDueDate = new Date(
            (currAssignmentSettings?.get('due_date') as string | number) || 0
          );

          if (latestDueDate < currentDueDate) {
            return curr ?? null;
          }
        }

        return latest;
      },
      null as ClassroomRelation | null
    );

    return classroom as unknown as StudentClassroomModelV1;
  },

  /**
   * Student assignment helpers
   */

  getStudentStartTime(studentId?: string | null): string | null {
    checkStudentRelationshipEntityCount(this, studentId, 'getStudentStartTime');
    return getSingleStudentRelationship(this, studentId).getStartTime();
  },

  hasStudentStartedAssignment(studentId?: string | null): boolean {
    return this.getStudentStartTime(studentId) !== null;
  },

  getStudentSubmittedAt(studentId?: string | null): string {
    checkStudentRelationshipEntityCount(this, studentId, 'getStudentSubmittedAt');
    return getSingleStudentRelationship(this, studentId).getSubmitted();
  },

  hasStudentSubmittedAssignment(studentId?: string | null): boolean {
    return this.getStudentSubmittedAt(studentId) !== null;
  },

  isAssignmentOpenForStudent(studentId?: string | null, classroomId?: string): boolean {
    checkStudentRelationshipEntityCount(this, studentId, 'isAssignmentOpenForStudent');

    // If the student has submitted the assignment, it is not open
    if (this.hasStudentSubmittedAssignment(studentId)) {
      return false;
    }
    // If the student has not submitted and the assignment has no due date, it's open
    if (
      !this.getDueDateFromSettings({
        classroomId,
        studentId
      })
    ) {
      return true;
    }
    // If the student has not submitted, but the assignment allows late submissions, it's open
    if (
      this.getIsAllowLateSubmissionsFromSettings({
        classroomId,
        studentId
      })
    ) {
      return true;
    }

    // If the student has not submitted, and the due date is in the future, it's open
    if (
      this.getDueDateFromSettings({
        classroomId,
        studentId
      }).isAfter(systemTimeOffsetStore.getCurrentTime())
    ) {
      return true;
    }
    // If the student has not submitted, but the due date is in the past, it's closed
    return false;
  },

  setStartDate(value: string): AssignmentModelV3 {
    if (value === 'now') {
      return this.setField('start_date', value);
    }

    const startDate = moment(value);
    const startTime = startDate.format('HH:mm:ss');

    const startDateToUnixTimestamp = startDate.isValid()
      ? TimeUtil.convertDateAndTimeToUnixTimestamp({
          date: startDate.format('YYYY-MM-DD'),
          time: startTime
        })
      : null;

    return this.setField('start_date', startDateToUnixTimestamp);
  },

  getPercentageCorrect(): number {
    return stats.percentage(
      this.getMeta().getStudentCountOfCorrectGuesses(),
      this.getMeta().getStudentCountOfGuesses()
    );
  },

  getStudentTimeSpentMinutes(idsToSelect: IdsToSelect = {}): string {
    if (this.hasStudentStartedAssignment() === false) {
      /**
       * If they haven't started, it's always 0!
       */
      return humanReadableDurationString(moment.duration(0));
    }

    const timeLimit: number | null = this.getTimeLimitFromSettings(idsToSelect);
    if (timeLimit === null) {
      /**
       * If there isn't a time limit on the assignment, we **ALWAYS** return the time spent from the meta
       * which is calculated from the `timeElapsed` on each individual guess.
       */
      return humanReadableDurationString(moment.duration(this.getMeta().getStudentTimeSpent()));
    }
    /**
     * At this point, we're only dealing with timed assignments.
     */
    const studentStartTime = this.getStudentStartTime(idsToSelect.studentId);
    const studentEndTime = this.hasStudentSubmittedAssignment(idsToSelect.studentId)
      ? moment(this.getStudentSubmittedAt(idsToSelect.studentId))
      : systemTimeOffsetStore.getCurrentTime();
    /**
     * Since it's a timed assignment, we always take the Math.min of these values to account for the fact
     * that students are able to submit _after_ the timer runs out.
     */
    const minutes = Math.min(
      moment(studentEndTime).diff(moment(studentStartTime), 'minutes'),
      timeLimit
    );
    return humanReadableDurationString(moment.duration(minutes, 'minutes'));
  },

  /**
   * NOTE: THE FOLLOWING METHODS MAY DO THE SAME THING AS OTHER METHODS IN THIS FILE.
   * IDENTIFY WHICH ONES ARE DUPLICATED, WHERE THEY ARE BEING USED, AND CONSOLIDATE
   */
  /**
   * IMPORTANT** This method expects assignments_v* metadata and a student ID passed to metacontext
   */
  isComplete(): boolean {
    return this.getMeta().getCountOfQuestions() === this.getMeta().getStudentCountOfGuesses();
  },

  /**
   * An assignment in "over due" if the due date has passed, and it does *NOT* allow for lat submission.
   *
   * @param idsToSelect
   */
  isOverDue(idsToSelect: IdsToSelect = {}): boolean {
    return (
      this.isPastDue(idsToSelect) === true &&
      this.getIsAllowLateSubmissionsFromSettings(idsToSelect) === false
    );
  },

  /**
   * An assignment is "past due" if the due date has passed.
   * This method does *NOT* account for assignments that allow late submissions – see `isOverDue`.
   *
   * @param idsToSelect
   */
  isPastDue(idsToSelect: IdsToSelect = {}): boolean {
    const dueDate = this.getDueDateFromSettings(idsToSelect);
    return dueDate !== null && systemTimeOffsetStore.getCurrentTime().isSameOrAfter(dueDate);
  },

  /* mainly a helper for the due date duration checks below */
  isDueIn(duration: number, timeframe: string, idsToSelect: IdsToSelect = {}): boolean {
    return (
      !this.isPastDue() &&
      this.getDueDateFromSettings(idsToSelect) <
        systemTimeOffsetStore.getCurrentTime().add(duration, timeframe)
    );
  },

  isDueIn1Day(idsToSelect: IdsToSelect = {}): boolean {
    return this.isDueIn(1, 'day', idsToSelect);
  },

  isDueIn1Week(idsToSelect: IdsToSelect = {}): boolean {
    return this.isDueIn(1, 'week', idsToSelect);
  },

  isDueIn1Month(idsToSelect: IdsToSelect = {}): boolean {
    return this.isDueIn(1, 'month', idsToSelect);
  },

  /* gets a duration from now until the due date */
  getTimeLeftUntilDue(idsToSelect: IdsToSelect = {}): moment.Duration {
    return moment.duration(
      this.getDueDateFromSettings(idsToSelect).valueOf() -
        systemTimeOffsetStore.getCurrentTime().valueOf()
    );
  },

  isScheduled(idsToSelect: IdsToSelect = {}): boolean {
    const startDate = this.getStartDateFromSettings(idsToSelect);
    return (
      this.isAssigned() &&
      startDate !== null &&
      systemTimeOffsetStore.getCurrentTime().isBefore(startDate)
    );
  },

  /**
   * IMPORTANT** This method expects assignments_v* metadata and a student ID passed to metacontext
   *
   * @param idsToSelect
   */
  isStudentSubmissionLate(idsToSelect: IdsToSelect = {}): boolean {
    const timeOfSubmission = this.getMeta().getStudentSubmittedAt();
    return Boolean(
      timeOfSubmission &&
      moment(timeOfSubmission).isSameOrAfter(this.getDueDateFromSettings(idsToSelect))
    );
  },

  /**
   * IMPORTANT** This method expects assignments_v* metadata and a student ID passed to metacontext
   *
   * @param idsToSelect
   */
  isStudentSubmissionOnTime(idsToSelect: IdsToSelect = {}): boolean {
    const timeOfSubmission = this.getMeta().getStudentSubmittedAt();
    return Boolean(
      timeOfSubmission &&
      moment(timeOfSubmission).isSameOrBefore(this.getDueDateFromSettings(idsToSelect))
    );
  },

  /**
   * Returns a boolean value depicting whether or not guess feedback (explanations)
   * should be shown based on the assignment's explanation setting and state.
   *
   * IMPORTANT* This method does rely on the inclusion of metadata (meta.student_submitted)
   *
   * @param idsToSelect
   */
  shouldShowGuessFeedback(idsToSelect: IdsToSelect = {}): boolean {
    const setting = this.getCorrectAnswerSettingFromSettings(idsToSelect);
    return (
      /**
       * If it's SHOW_AFTER_EACH, assume this method is being called in scenarios
       * where a guess has been placed for a given question.
       */
      setting === assignmentConstants.CORRECT_ANSWER_SETTINGS.SHOW_AFTER_EACH.VALUE ||
      (setting === assignmentConstants.CORRECT_ANSWER_SETTINGS.SHOW_AFTER_ALL.VALUE &&
        this.getMeta().isStudentSubmitted()) ||
      (setting === assignmentConstants.CORRECT_ANSWER_SETTINGS.SHOW_AFTER_DUE_DATE.VALUE &&
        this.isPastDue(idsToSelect) &&
        (this.getMeta().isStudentSubmitted() ||
          this.getIsAllowLateSubmissionsFromSettings(idsToSelect) === false))
    );
  },

  /**
   * Teacher assignment helpers
   */
  getStudentsByMasteryBuckets(): Map<string, List<unknown>> {
    const func = () => {
      const initialBuckets = (Map(BUCKETS).map(() => List()) as Map<string, List<unknown>>).set(
        'NOT_AVAILABLE',
        List()
      );
      return this.getStudents().reduce(
        // Immutable 3.8 types the reduce accumulator as optional; `acc` is never undefined at
        // runtime (an initial value is always passed), so fall back to the initial buckets Map.
        (acc: Map<string, List<unknown>> | undefined, student) => {
          const buckets = acc ?? initialBuckets;
          const studentMeta = student!.getMeta();
          const correctGuessCount = studentMeta.getAssignmentCountOfCorrectGuesses();
          const incorrectGuessCount = studentMeta.getAssignmentCountOfIncorrectGuesses();
          const totalGuesses = correctGuessCount + incorrectGuessCount;

          const updater = (
            bucket: string
          ): [string, (studentsList: List<unknown>) => List<unknown>] => [
            bucket,
            (studentsList) => studentsList.push(student)
          ];

          if (totalGuesses === 0) {
            return buckets.update(...updater('NOT_AVAILABLE'));
          }

          const accuracy = stats.percentage(correctGuessCount, totalGuesses);
          const bucket = getBucketByAccuracy(accuracy);

          return buckets.update(...updater(bucket.BUCKET_NAME));
        },
        initialBuckets
      );
    };
    return this.cache('getStudentsByMasteryBuckets', func);
  }
} satisfies ThisType<AssignmentExtensionContext>;

function checkStudentRelationshipEntityCount(assignment, studentId, methodName) {
  const relationships = assignment.getStudentRelationships();
  if (relationships.size > 1 && !studentId) {
    throw new Error(
      `Attempted to call ${methodName} on Assignment ${assignment.getName()} without passing a studentId, but the Assignment has multiple student relationships. Please specify a studentId.`
    );
  }
}

/**
 * In order to use certain extensions, we need to ensure that we have the appropriate assignment meta. Sadly,
 * depending on how you make your request, this meta can either be on the student, or on the assignment.
 * This function checks both cause that's what we gotta do.
 *
 * @param assignment
 * @param studentId
 */
function getSingleStudentRelationship(
  assignment,
  studentId?: string | null
): StudentAssignmentRelationship {
  let relationships;
  if (assignment.getStudentRelationships().isEmpty()) {
    relationships = studentId
      ? assignment
          .getStudents()
          .find((student) => student.getId() === studentId)
          .getAssignmentRelationships()
          .get(assignment.getId())
      : assignment.getStudents().first().getAssignmentRelationships().get(assignment.getId());
  } else {
    relationships = studentId
      ? assignment.getStudentRelationships().get(studentId)
      : assignment.getStudentRelationships().first();
  }
  return relationships;
}

export {assignmentExtensions};
